/**
 * English is the SOURCE language. Every other locale file is a Partial of
 * this shape; missing keys fall back to English at runtime.
 */
export const en = {
  meta: {
    title: "Revolin Roofing | Premium Roofing in Metro Vancouver, BC",
    description:
      "Roof replacement, repairs and premium roofing systems for Metro Vancouver homeowners. Detailed assessments, transparent scope, Revolin Group standards.",
  },
  nav: {
    services: "Services",
    roofTypes: "Roof Types",
    beforeAfter: "Before & After",
    areas: "Service Areas",
    why: "Why Revolin",
    faq: "FAQ",
    contact: "Contact",
    menu: "Menu",
    close: "Close menu",
    language: "Language",
  },
  cta: {
    primary: "Request a Roof Assessment",
    call: "Call Now",
    callBrand: "Call Revolin Roofing",
    learnMore: "Learn more",
  },
  hero: {
    eyebrow: "Metro Vancouver · Lower Mainland",
    titleLine1: "Roofing Built for BC.",
    titleLine2: "Finished to Revolin Standards.",
    body: "Revolin Roofing provides roof replacement, roof repair and premium roofing options for Metro Vancouver homeowners, with recommendations shaped by the home, roof conditions and applicable requirements.",
    imageCaption: "Standing-seam metal roof, North Shore architecture",
  },
  trust: {
    eyebrow: "Coverage & credentials",
    title: "Insured, registered and warrantied work",
    guaranteeMention:
      "Workmanship coverage is issued in writing on every job. Read the full guarantee details on our about page.",
    guaranteeLink: "Our Guarantee",
  },
  brands: {
    eyebrow: "Materials",
    title: "We install trusted brands",
    seeAll: "See materials we trust",
  },
  testimonials: {
    eyebrow: "Reviews",
    title: "What Homeowners Say",
    previewNotice: "Preview placeholders - not shown in production",
    googleCta: "Read more reviews on Google:",
    googleLinkLabel: "Google reviews",
  },
  services: {
    eyebrow: "Services",
    title: "Roofing services for Metro Vancouver homes",
    body: "From an urgent leak to a full architectural re-roof, every project starts with the same assessment discipline.",
    items: [
      {
        title: "Roof Replacement",
        intent: "My roof is at the end of its service life",
        body: "Full tear-off and re-roof with the assembly, underlayment, ventilation and flashing detailing suited to your home.",
      },
      {
        title: "Roof Repair & Leak Diagnosis",
        intent: "I have a leak or visible damage",
        body: "Assessment considers common leak paths such as flashing, penetrations and drainage before a repair scope is proposed.",
      },
      {
        title: "Architectural Asphalt Shingles",
        intent: "I want a proven, mainstream roof",
        body: "A strong residential option for sloped roofs, with a wide range of profiles and colours.",
      },
      {
        title: "Standing-Seam Metal Roofing",
        intent: "I want longevity and a modern look",
        body: "A premium option for contemporary and West Coast architecture, with concealed fasteners and crisp lines.",
      },
      {
        title: "Flat & Low-Slope Roofing",
        intent: "My roof is flat or low-slope",
        body: "Membrane systems specified for drainage, slope and detailing at parapets, curbs and transitions.",
      },
      {
        title: "Roof Inspection & Maintenance",
        intent: "I want to know where I stand",
        body: "Documented condition assessment and maintenance priorities to support informed planning.",
      },
    ],
  },
  comparison: {
    eyebrow: "Roof systems",
    title: "Which roof is right for your home?",
    body: "There is no single best roofing material. The right assembly depends on slope, architecture, exposure, drainage, budget and applicable local requirements. Here is how the main options generally compare.",
    columns: {
      application: "Ideal application",
      character: "Design character",
      maintenance: "Maintenance considerations",
      investment: "Relative investment",
      performance: "Performance characteristics",
    },
    systems: [
      {
        name: "Architectural Asphalt Shingles",
        summary: "A strong mainstream residential option.",
        application: "Sloped roofs on most Lower Mainland homes.",
        character: "Dimensional texture, traditional to transitional.",
        maintenance: "Periodic inspection; moss control matters in coastal climates.",
        investment: "Moderate",
        performance:
          "Well-understood system with broad product availability and straightforward future repairs.",
      },
      {
        name: "Standing-Seam Metal",
        summary: "A premium longevity and design option.",
        application: "Contemporary and West Coast architecture with clean roof planes.",
        character: "Crisp, linear, architectural.",
        maintenance: "Low routine maintenance; detailing and fastening quality are critical.",
        investment: "Higher",
        performance:
          "Concealed fasteners and continuous panels shed water well on exposed elevations.",
      },
      {
        name: "Flat & Low-Slope Membrane",
        summary: "The appropriate family of systems for low-slope areas.",
        application: "Flat and low-slope roof areas, decks and additions.",
        character: "Recessive: the roof is not a visual feature.",
        maintenance: "Drain and seam inspection; keep drainage paths clear.",
        investment: "Varies by membrane and assembly",
        performance:
          "Performance depends heavily on slope-to-drain, membrane selection and transition detailing.",
      },
    ],
    disclaimer:
      "Guidance only. Material selection and assembly detailing follow current RCABC guidance and applicable BC and municipal requirements. We do not provide engineering guarantees.",
  },
  why: {
    eyebrow: "Why Revolin Roofing",
    title: "The roofing division of a construction company",
    body: "Revolin Roofing, operating from 2025 as a Revolin Group company, applies the parent studio’s project approach to residential roofing: assessment first, a written proposed scope and attention to coastal-weather details. Our crew of 30 licensed contractors brings over 10 years of field experience to every job.",
    items: [
      {
        title: "Assessment before recommendation",
        body: "We document what we find before we propose a scope.",
      },
      {
        title: "Transparent scope and pricing",
        body: "The proposed scope identifies inclusions, exclusions and conditions that may require a change.",
      },
      {
        title: "Architectural attention",
        body: "Roof lines, colour and profile are chosen to suit the building.",
      },
      {
        title: "Flashing and transition detailing",
        body: "Transitions are common investigation points, so they receive deliberate attention during assessment and scoping.",
      },
      {
        title: "Site planning",
        body: "Access, protection and cleanup expectations are addressed for the specific property.",
      },
      {
        title: "Project communication",
        body: "Schedule and communication expectations are confirmed with the project scope.",
      },
      {
        title: "Quality-control checkpoints",
        body: "Defined checks during and at the end of the installation.",
      },
      {
        title: "Parent-company expertise",
        body: "Access to Revolin Group's broader construction capability when a roof issue is not only a roof issue.",
      },
    ],
  },
  beforeAfter: {
    eyebrow: "Before & After",
    title: "See the difference in the detail",
    body: "Drag the handle to compare before-and-after roofing transformations for Metro Vancouver homes.",
    beforeLabel: "Before",
    afterLabel: "After",
    sliderLabel: "Before and after comparison slider",
  },
  process: {
    eyebrow: "Process",
    title: "How a Revolin roofing project runs",
    steps: [
      {
        n: "01",
        title: "Roof Assessment",
        body: "We inspect the roof, flashings, drainage and interior evidence, and document the condition.",
      },
      {
        n: "02",
        title: "Scope & Roofing Recommendation",
        body: "The proposal can document suitable options, the reasoning, scope and pricing for review.",
      },
      {
        n: "03",
        title: "Installation / Repair",
        body: "Approved work proceeds to the documented scope, site plan and applicable project requirements.",
      },
      {
        n: "04",
        title: "Final Inspection & Handover",
        body: "Completion requirements and any project documentation follow the agreed scope.",
      },
    ],
  },
  weather: {
    eyebrow: "BC conditions",
    title: "A roof here is a water-management system",
    body: "In the Lower Mainland a roof spends much of the year managing prolonged rain rather than sun. That changes what matters.",
    items: [
      {
        title: "Prolonged rain and moisture",
        body: "Underlayment, laps and ventilation carry more weight in a wet coastal climate.",
      },
      {
        title: "Wind and exposure",
        body: "Exposed elevations and open sites influence fastening and edge detailing.",
      },
      {
        title: "Drainage",
        body: "Slope-to-drain, gutters and downspout capacity decide where water goes under load.",
      },
      {
        title: "Snow where applicable",
        body: "Higher elevations in the region see snow loads and meltwater cycles that flatter sites do not.",
      },
      {
        title: "Roof geometry",
        body: "Valleys, dormers, skylights and additions create the transitions that need the most care.",
      },
    ],
  },
  areas: {
    eyebrow: "Service areas",
    title: "Serving Metro Vancouver and the Lower Mainland",
    body: "If your community is nearby but not listed, contact us and we will confirm availability.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Homeowner questions",
    items: [
      {
        q: "Do I need a roof repair or a full replacement?",
        a: "It depends on the age and condition of the assembly and whether the problem is isolated. A localized issue may support a repair scope, while an older covering or multiple failure points may justify considering replacement. An assessment helps document which options fit the observed conditions.",
      },
      {
        q: "My roof is leaking. What should I do first?",
        a: "Contain the water inside, photograph what you can see safely, and contact us. Do not go on a wet roof. Leaks frequently originate away from the visible stain, so the diagnosis matters more than the patch.",
      },
      {
        q: "What happens during a roof assessment?",
        a: "We review the roof covering, flashings, penetrations, ventilation and drainage, look for interior evidence where relevant, and document findings. You then receive a written scope and recommendation.",
      },
      {
        q: "Which roofing material is best?",
        a: "No material is best for every home. Slope, architecture, exposure, drainage, budget and applicable local requirements all factor in. We explain the trade-offs and recommend an assembly, rather than pushing one product.",
      },
      {
        q: "Can you work on flat and low-slope roofs?",
        a: "Yes. Low-slope areas are specified as membrane systems, with particular attention to slope-to-drain and detailing at parapets, curbs and transitions.",
      },
      {
        q: "Metal or shingles?",
        a: "Architectural asphalt shingles are a strong mainstream residential option. Standing-seam metal is a premium longevity and design option that suits contemporary and West Coast architecture. Both can be correct - it depends on the building and your priorities.",
      },
      {
        q: "How long does a roofing project take?",
        a: "Timeline depends on size, complexity, access and the system selected. We provide the expected duration with your scope, and update you if conditions change.",
      },
      {
        q: "How do I get a quote?",
        a: "Request a roof assessment. We will look at the roof, then provide a written scope and price. We do not quote a re-roof without seeing the roof.",
      },
      {
        q: "What happens if it rains during my project?",
        a: "Weather can affect safe work, sequencing and schedule. The protection approach and any weather-related changes are handled through the project-specific plan and communication process.",
      },
      {
        q: "What warranty applies?",
        a: "Projects include a written Revolin workmanship warranty and the applicable manufacturer material warranty for the system installed. Summaries are on the About page under Our Guarantee; the controlling terms are always the documents issued with your project.",
      },
      {
        q: "Which areas do you serve?",
        a: "Metro Vancouver and the Lower Mainland, including the communities listed on this page. Contact us to confirm availability nearby.",
      },
      {
        q: "Do you handle commercial and strata roofing?",
        a: "Revolin Roofing publishes dedicated commercial and strata roofing pathways that connect to established repair, maintenance and low-slope services. Each building is assessed on its own conditions, access and decision process before a written scope is prepared.",
      },
      {
        q: "What is Revolin Roofing’s relationship to Revolin Group?",
        a: "Revolin Roofing is a Revolin Group company focused on roofing for Metro Vancouver and the Lower Mainland. Revolin Group handles broader luxury renovations and custom homes; roofing enquiries stay on this site and phone line.",
      },
      {
        q: "How does the rainy season affect roofing work?",
        a: "Metro Vancouver’s wet months make drainage, temporary protection and safe access especially important. We sequence work around weather and communicate schedule changes through the project plan rather than pushing unsafe installation.",
      },
    ],
  },
  form: {
    eyebrow: "Request an assessment",
    title: "Request a Roof Assessment",
    body: "Tell us about the property, the roof and what you have noticed.",
    name: "Full name",
    phone: "Phone",
    email: "Email",
    postal: "Postal code",
    need: "Roofing need",
    needOptions: [
      "Emergency leak / active water entry",
      "Roof repair / flashing",
      "Asphalt shingle replacement",
      "Metal roofing",
      "Flat or low-slope roof",
      "Cedar roofing",
      "Gutters / fascia / ventilation",
      "Moss removal / maintenance",
      "Roof health check / inspection",
      "Not sure yet",
    ],
    timeline: "Preferred timeline",
    timelineOptions: ["As soon as possible", "Within 1–3 months", "3–6 months", "Just planning"],
    message: "Describe the problem (optional)",
    submit: "Request a Roof Assessment",
    submitting: "Sending…",
    successTitle: "Request received",
    successBody: "Thank you. Your roof assessment request was delivered successfully.",
    errorRequired: "This field is required",
    errorEmail: "Enter a valid email address",
    errorPhone: "Enter a valid phone number",
    errorSubmit: "We could not send your request. Please use the contact details on this page.",
    errorUnavailable:
      "Online requests are temporarily unavailable. Please use the contact details on this page.",
    privacy: "By submitting, you agree to be contacted about your roofing enquiry.",
    privacyLink: "Privacy policy",
  },
  finalCta: {
    title: "Your roof protects everything beneath it.",
    body: "Start with an assessment. You will know the condition of your roof, the options available and what each one involves.",
  },
  footer: {
    servicesTitle: "Services",
    areasTitle: "Service Areas",
    contactTitle: "Contact",
    privacy: "Privacy",
    terms: "Terms",
    rights: "All rights reserved.",
    disclaimer:
      "Roofing guidance on this site is general information, not an engineering opinion. Recommendations are confirmed after an on-site assessment.",
  },
  sticky: {
    call: "Call",
    quote: "Request Quote",
  },
};

export type Dictionary = typeof en;
